import React, { useState, useEffect } from "react";
import { ArrowRight, Copy, Check, Download } from "lucide-react";
import { PATTERNS, SUPPORTING_COPY, SHARE_TEXT, scoreLabel } from "./quizData";
import { PROFILE_BY_KEY } from "./quizProfiles.jsx";

const CALENDLY_URL = "https://calendly.com/lizdirocco/new-meeting";

const PDF_URL_BY_PATTERN = {
  upgrader: "https://drive.google.com/uc?export=download&id=1_0ctY1RmApZ46eFXv95OMK5owiIMi1j-",
  exposed: "https://drive.google.com/uc?export=download&id=1U1wqKn5k9veHddCSE2HBTDg9I7wM9y4i",
  performer: "https://drive.google.com/uc?export=download&id=1AujPPyRyQBQDu1Lz4FLOK2FrhI0vqXBx",
  uninformed: "https://drive.google.com/uc?export=download&id=1HwA6y8cmi0jmpPXW8DEY_mMcH0_1AbBi",
};

// Each section corresponds to a pattern in display order on dashboard
const ORDER = [
  { sectionKey: "s1", pattern: "upgrader" },
  { sectionKey: "s2", pattern: "exposed" },
  { sectionKey: "s3", pattern: "performer" },
  { sectionKey: "s4", pattern: "uninformed" },
];

function ScoreBlock({ pattern, score, isDominant, animate }) {
  const pct = (score / 16) * 100;
  const label = scoreLabel(score);
  return (
    <div
      data-testid={`score-block-${pattern}`}
      className={`p-6 md:p-7 transition-colors ${
        isDominant
          ? "bg-nearblack text-paper border-[2px] border-camel"
          : "bg-paper border border-paperdeep"
      }`}
    >
      <p
        className={`uppercase tracking-[0.22em] ${isDominant ? "text-camellight" : "text-stonegrey"}`}
        style={{ fontSize: "10.5px", fontWeight: 500 }}
      >
        {isDominant ? "Dominant Pattern" : "Pattern"}
      </p>
      <p
        className={`font-serif mt-2 ${isDominant ? "text-paper" : "text-nearblack"}`}
        style={{ fontSize: "clamp(1.4rem, 2vw, 1.7rem)", fontWeight: 400 }}
      >
        {PATTERNS[pattern].name}
      </p>
      <div className="mt-5">
        <div
          className={`h-[3px] w-full ${isDominant ? "bg-paper/15" : "bg-stonegrey/25"}`}
        >
          <div
            className="h-full bg-camel transition-[width] duration-[800ms] ease-out"
            style={{ width: animate ? `${pct}%` : "0%" }}
          />
        </div>
        <div className="mt-3 flex items-baseline justify-between">
          <span
            className={`uppercase tracking-[0.22em] ${
              isDominant ? "text-camellight" : "text-stonegrey"
            }`}
            style={{ fontSize: "10.5px", fontWeight: 500 }}
          >
            {label}
          </span>
          <span
            className={`font-serif ${isDominant ? "text-paper" : "text-nearblack"}`}
            style={{ fontSize: "1.15rem" }}
          >
            {score}<span className={isDominant ? "text-camellight/70" : "text-stonegrey/70"}> / 16</span>
          </span>
        </div>
      </div>
    </div>
  );
}

export default function QuizResults({ scores, dominant, firstName }) {
  const [animate, setAnimate] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimate(true), 120);
    return () => clearTimeout(t);
  }, []);

  const sectionByPattern = {
    upgrader: scores.s1,
    exposed: scores.s2,
    performer: scores.s3,
    uninformed: scores.s4,
  };
  const Profile = PROFILE_BY_KEY[dominant];
  const dominantInfo = PATTERNS[dominant];

  const copyShare = async () => {
    try {
      const text = SHARE_TEXT[dominant];
      if (navigator.share) {
        try {
          await navigator.share({ title: "The Wealth Identity Assessment", text, url: "https://lizdirocco.com/quiz" });
        } catch {
          await navigator.clipboard.writeText(text);
        }
      } else {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* noop */ }
  };

  const supportingPatterns = ORDER.filter((o) => o.pattern !== dominant);

  return (
    <div className="bg-paper text-nearblack" data-testid="quiz-results">
      {/* ---------- Section 1: Score Dashboard ---------- */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-20">
        <div className="max-w-[960px] mx-auto px-6 md:px-10">
          <p
            className="uppercase text-stonegrey text-center"
            style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
          >
            Your Wealth Identity Results
          </p>
          {firstName && (
            <p
              className="font-serif italic text-stonegrey text-center mt-4"
              style={{ fontSize: "clamp(1.1rem, 1.8vw, 1.3rem)", fontWeight: 300 }}
            >
              For {firstName}.
            </p>
          )}
          <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {ORDER.map((o) => (
              <ScoreBlock
                key={o.pattern}
                pattern={o.pattern}
                score={sectionByPattern[o.pattern]}
                isDominant={o.pattern === dominant}
                animate={animate}
              />
            ))}
          </div>
          <p className="font-serif italic text-stonegrey text-center mt-10 text-[15px]">
            Your dominant pattern is the one quietly running most of your financial decisions.
          </p>
        </div>
      </section>

      {/* ---------- Section 2: Dominant Reveal + Deep-dive Profile ---------- */}
      <section className="pb-24 md:pb-32 border-t border-paperdeep pt-20 md:pt-28">
        <div className="max-w-[720px] mx-auto px-6 md:px-10">
          <p
            className="uppercase text-stonegrey"
            style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
          >
            Your Dominant Wealth Pattern
          </p>
          <h2
            data-testid="dominant-name"
            className="font-serif text-nearblack mt-4 leading-[1.05]"
            style={{ fontSize: "clamp(2.5rem, 6.5vw, 4.5rem)", fontWeight: 300 }}
          >
            {dominantInfo.name}
          </h2>
          <p
            className="text-camel mt-6"
            style={{ fontSize: "clamp(1.15rem, 1.6vw, 1.25rem)", lineHeight: 1.5 }}
          >
            {dominantInfo.summary}
          </p>
          <span className="block w-[60px] h-px bg-camel mt-10 mb-2" />
          <div className="mt-10" data-testid="dominant-profile">
            <Profile />
          </div>
        </div>
      </section>

      {/* ---------- Section 3: Supporting Patterns ---------- */}
      <section className="bg-paper py-20 md:py-28 border-t border-paperdeep">
        <div className="max-w-[960px] mx-auto px-6 md:px-10">
          <h3
            className="font-serif text-nearblack leading-[1.1]"
            style={{ fontSize: "clamp(1.7rem, 3vw, 2.1rem)", fontWeight: 400 }}
          >
            Your other Wealth Patterns
          </h3>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {supportingPatterns.map((p) => {
              const sc = sectionByPattern[p.pattern];
              const label = scoreLabel(sc);
              return (
                <article
                  key={p.pattern}
                  data-testid={`supporting-${p.pattern}`}
                  className="bg-paper border border-paperdeep p-6 md:p-7"
                >
                  <p
                    className="uppercase text-stonegrey"
                    style={{ fontSize: "10.5px", letterSpacing: "0.22em", fontWeight: 500 }}
                  >
                    {label} · {sc}/16
                  </p>
                  <p
                    className="font-serif text-nearblack mt-2"
                    style={{ fontSize: "clamp(1.25rem, 1.6vw, 1.4rem)", fontWeight: 400 }}
                  >
                    {PATTERNS[p.pattern].name}
                  </p>
                  <p className="mt-4 text-nearblack/75 text-[14.5px] leading-[1.7] font-light">
                    {SUPPORTING_COPY[p.pattern][label]}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- Section 3.5: PDF Download ---------- */}
      <section className="bg-paper py-14 md:py-16 border-t border-paperdeep">
        <div className="max-w-[720px] mx-auto px-6 md:px-10 text-center">
          <p
            className="uppercase text-stonegrey"
            style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
          >
            Keep Your Results
          </p>
          <p
            className="font-serif italic text-camel mt-4"
            style={{ fontSize: "clamp(1.25rem, 2.2vw, 1.55rem)", fontWeight: 300 }}
          >
            Your full results — yours to keep.
          </p>
          <a
            href={PDF_URL_BY_PATTERN[dominant]}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="results-download-pdf"
            className="mt-7 inline-flex items-center justify-center gap-2 bg-camel hover:bg-camellight text-nearblack transition-colors"
            style={{
              height: "56px",
              padding: "0 32px",
              fontSize: "16px",
              fontWeight: 600,
              borderRadius: "8px",
              minWidth: "min(400px, 100%)",
            }}
          >
            <Download className="w-4 h-4" />
            Download Your Results PDF
          </a>
        </div>
      </section>

      {/* ---------- Section 4: Share Prompt ---------- */}
      <section className="bg-paper py-16 md:py-20 border-t border-paperdeep">
        <div className="max-w-[720px] mx-auto px-6 md:px-10">
          <div className="bg-paper border border-camel/50 p-6 md:p-7">
            <p className="text-nearblack font-medium" style={{ fontSize: "15px" }}>
              Know someone who should take this?
            </p>
            <p className="text-stonegrey mt-2 text-[14.5px] leading-[1.7] font-light">
              The Wealth Identity Assessment was built for people earning well and feeling stuck. If
              someone in your life fits that description, send them this.
            </p>
            <div
              data-testid="share-text"
              className="mt-5 bg-white p-4 text-nearblack text-[14px] leading-[1.7]"
            >
              {SHARE_TEXT[dominant]}
            </div>
            <button
              type="button"
              onClick={copyShare}
              data-testid="share-copy"
              className="mt-5 inline-flex items-center gap-2 border border-camel text-camel hover:bg-camel hover:text-paper px-5 py-3 transition-colors uppercase tracking-[0.22em]"
              style={{ fontSize: "11px", fontWeight: 600 }}
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy Share Link"}
            </button>
          </div>
        </div>
      </section>

      {/* ---------- Section 5: CTA Block ---------- */}
      <section className="bg-nearblack text-paper py-20 md:py-28">
        <div className="max-w-[720px] mx-auto px-6 md:px-10 text-center">
          <p
            className="uppercase text-camel"
            style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
          >
            Your Next Step
          </p>
          <h2
            className="font-serif text-paper mt-5 leading-[1.05]"
            style={{ fontSize: "clamp(2rem, 4.5vw, 2.85rem)", fontWeight: 300 }}
          >
            The Money Clarity Call
          </h2>
          <p className="mt-7 text-paper/85 text-[17px] leading-[1.75] font-light max-w-[520px] mx-auto">
            A free 30-minute conversation built for exactly where you are.
          </p>
          <p className="mt-5 text-paper/80 text-[16px] leading-[1.75] font-light max-w-[520px] mx-auto">
            We&apos;ll look at what your dominant pattern is actually costing you, what one
            structural shift would change your financial trajectory, and whether the strategies I
            work with could be useful in your specific situation.
          </p>
          <p className="mt-5 text-paper/80 text-[16px] leading-[1.75] font-light max-w-[520px] mx-auto">
            No products. No pressure. Just clarity.
          </p>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="results-book-cta"
            className="mt-10 inline-flex items-center justify-center gap-2 bg-camel hover:bg-camellight text-nearblack px-8 transition-colors"
            style={{ height: "56px", fontWeight: 600, fontSize: "17px", borderRadius: "8px", minWidth: "min(400px, 100%)" }}
          >
            Book a Money Clarity Call
            <ArrowRight className="w-4 h-4" />
          </a>
          <p className="mt-5 text-stonegrey text-[13px] italic">
            30 minutes · Free · Specific to your situation
          </p>
        </div>
      </section>
    </div>
  );
}
